/**
 * Turn a `getUserMedia` rejection into copy that names the actual cause.
 *
 * Every microphone surface used to collapse all of these into one string —
 * "Unable to access microphone. Please check permissions." — which is wrong
 * for most of them and unactionable for the one it's right about:
 *
 * - A DENIED permission is the case the copy addresses, and it is also the
 *   case where "check permissions" is least useful, because Chrome and Safari
 *   will NOT re-prompt after a denial. Retrying does nothing; the user has to
 *   change a site setting they have no reason to know exists. A customer hit
 *   this on 2026-08-18, gave up on voice entirely, and typed their protocol
 *   out by hand.
 * - No microphone attached, a device another app is holding, an insecure
 *   origin, and the Meta-glasses HFP DOMException `useVoiceRecording` documents
 *   three lines above its own `getUserMedia` call are all NOT permission
 *   problems, and telling someone to check permissions sends them somewhere
 *   with nothing to fix.
 *
 * `reason` is the DOMException name, for the analytics property — it is a
 * bounded browser enum, carries no user content, and is the only way to answer
 * "was that actually a denial?" from data rather than from a session replay.
 */
export interface MicrophoneErrorCopy {
  readonly title: string;
  readonly description: string;
  /** DOMException name, or 'UnknownError'. Safe to send to analytics. */
  readonly reason: string;
}

/**
 * Whether the current runtime actually shows a browser address bar.
 *
 * The denial copy used to name the address-bar icon unconditionally, which is
 * the precise, useful instruction on desktop and an impossible one on the two
 * runtimes mobile quick capture actually runs in: the manifest is
 * `"display": "standalone"` with a shortcut straight to `/mobile/capture` (no
 * browser toolbar), and the `BowerApp` Android WebView has no omnibox — there
 * the fix lives in the OS app settings. Both are already detected elsewhere in
 * the app (`useMobileDetection`, `MobileLayout`); this is the same test, kept
 * local so the describer stays a plain function every call site can share.
 *
 * Guarded for non-DOM callers (unit tests, SSR-ish contexts) rather than
 * assumed — a `window`-less environment gets the browser wording, which is the
 * status quo for every desktop surface.
 */
function hasBrowserAddressBar(): boolean {
  // Every read is optional, and that is not defensive padding: this runs INSIDE
  // an error handler, so anything that throws here replaces the user's error
  // message with nothing at all. `navigator.userAgent` in particular is a
  // prototype getter that does not survive the `{...navigator}` spread some
  // test harnesses use, and `matchMedia` is absent in non-DOM environments.
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
    if (navigator.userAgent?.includes('BowerApp')) return false;
    const standalone =
      (navigator as unknown as { standalone?: boolean }).standalone === true ||
      window.matchMedia?.('(display-mode: standalone)')?.matches === true;
    return !standalone;
  } catch {
    // Fall back to the browser wording — the status quo for every desktop
    // surface, and the larger audience.
    return true;
  }
}

/**
 * Browsers disagree on these names. Firefox still emits the legacy
 * `PermissionDeniedError`/`DevicesNotFoundError` spellings, and `AbortError` is
 * the spec's catch-all that Firefox is best documented to use for a device that
 * is present but unusable, so each case matches on the whole family rather than
 * the one name Chrome happens to use. An unmatched name falls through to the
 * generic branch WITH its name attached, so a spelling we haven't seen shows up
 * in analytics as itself instead of being silently absorbed into "denied".
 */
export function describeMicrophoneError(error: unknown): MicrophoneErrorCopy {
  const reason = error instanceof Error && error.name ? error.name : 'UnknownError';

  switch (reason) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return {
        reason,
        title: 'Microphone access is blocked',
        // Names the control rather than saying "check permissions", because
        // nothing will ask again — this is the only way back. Which control
        // that is depends on the runtime; see hasBrowserAddressBar above.
        description: hasBrowserAddressBar()
          ? 'Your browser is blocking the microphone for Bower. Click the icon at the left of ' +
            'the address bar, allow the microphone, then start recording again.'
          : 'Bower is blocked from using the microphone. Allow it for Bower in your device ' +
            'settings, then start recording again.',
      };

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return {
        reason,
        title: 'No microphone found',
        description: 'Connect a microphone or headset, then start recording again.',
      };

    // `NotReadableError`/`TrackStartError` are specifically a device that
    // cannot be read. `AbortError` is the spec's catch-all ("some problem
    // occurred which prevented the device from being used") — permission was
    // granted and the hardware is fine, it still failed. Grouping it here is a
    // bet on the most probable recovery, not a diagnosis, so the copy hedges
    // rather than asserting another app definitely holds the mic.
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return {
        reason,
        title: 'Your microphone is in use',
        description:
          'Something is holding your microphone — usually another app or tab, and video calls ' +
          'are the usual culprit. Close it, then start recording again.',
      };

    case 'SecurityError':
      return {
        reason,
        title: 'Recording needs a secure connection',
        description: 'Open Bower over https and try again.',
      };

    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return {
        reason,
        title: "This microphone isn't supported",
        description: 'Try a different microphone, or record on another device.',
      };

    default:
      // An insecure origin does NOT arrive as `SecurityError`. The browser
      // omits `navigator.mediaDevices` entirely, so `mediaDevices.getUserMedia`
      // throws a plain TypeError and lands here. (The spec's `SecurityError`
      // means media support is disabled on the Document — a different and
      // near-unreachable case, kept above for completeness.) Detected by the
      // absent API rather than by sniffing the TypeError's message, which is
      // browser-specific.
      //
      // BOTH halves of the condition are load-bearing: keying on the missing
      // API alone swallows every unrecognised DOMException in any environment
      // without the API — jsdom, for one — which is the opposite of this
      // branch's purpose, and is exactly what the first version of it did.
      //
      // Prod is https and localhost is a secure context, so this is a dev/QA
      // path — and a live one here, since this repo's dev config points the
      // frontend at a LAN IP over http.
      if (reason === 'TypeError' && typeof navigator !== 'undefined' && !navigator.mediaDevices) {
        return {
          reason,
          title: 'Recording needs a secure connection',
          description:
            'Browsers only allow microphone access over https or localhost. Open Bower on ' +
            'localhost, or over https, and try again.',
        };
      }
      return {
        reason,
        title: "Couldn't start recording",
        description:
          'Bower could not open your microphone. Check that no other app is using it, then try ' +
          'again. If it keeps happening, let us know.',
      };
  }
}
